import React from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import Loading from './Loading';
import EmptyState from './EmptyState';

const Table = ({
  columns,
  data,
  loading = false,
  sortBy,
  sortOrder,
  onSort,
  selectable = false,
  selectedRows = [],
  onSelectRow,
  onSelectAll,
  emptyMessage = 'No data found',
  emptyIcon,
}) => {
  const handleSort = (column) => {
    if (!column.sortable || !onSort) return;
    const newOrder = sortBy === column.key && sortOrder === 'ASC' ? 'DESC' : 'ASC';
    onSort(column.key, newOrder);
  };

  const isAllSelected = data.length > 0 && selectedRows.length === data.length;

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-8">
        <Loading />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-2xl">
        <EmptyState message={emptyMessage} icon={emptyIcon} />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              {selectable && (
                <th className="w-12">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={(e) => onSelectAll?.(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`${column.sortable ? 'cursor-pointer hover:bg-slate-100' : ''} ${column.className || ''}`}
                  onClick={() => handleSort(column)}
                  style={{ width: column.width }}
                >
                  <div className="flex items-center gap-2">
                    <span>{column.title}</span>
                    {column.sortable && (
                      <div className="flex flex-col">
                        <ChevronUpIcon
                          className={`w-3 h-3 ${sortBy === column.key && sortOrder === 'ASC' ? 'text-indigo-600' : 'text-slate-300'}`}
                        />
                        <ChevronDownIcon
                          className={`w-3 h-3 -mt-1 ${sortBy === column.key && sortOrder === 'DESC' ? 'text-indigo-600' : 'text-slate-300'}`}
                        />
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr key={row.id || rowIndex} className="hover:bg-slate-50 transition-colors">
                {selectable && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(row.id)}
                      onChange={(e) => onSelectRow?.(row.id, e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  </td>
                )}
                {columns.map((column) => (
                  <td key={column.key} className={column.cellClassName || ''}>
                    {column.render ? column.render(row[column.key], row, rowIndex) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Table;
